import { useState } from "react";
import { ATTENDANCE_TITLES, type Attendance, type ScheduleEntry } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { marksView, monthChipText, monthMarkNote, nextAttendance } from "./entry-marks.ts";

/**
 * Полоса отметок занятия: был ли ученик и заплачено ли.
 *
 * Своим файлом с 03.09.2026: карточка занятия дошла до предела в 400 строк
 * (раздел 9 CLAUDE.md), а полоса это связный кусок с собственным состоянием и
 * собственными правилами видимости. Граница проходит там же, где смысловая:
 * карточка ОПИСЫВАЕТ занятие, а здесь про него делается ЗАПИСЬ.
 *
 * ⚠️ **Кому и что здесь видно, решает `marksView`** (`entry-marks.ts`), и здесь
 * это правило не повторяется ни одной веткой: видимость полосы это приватность
 * (записи преподавателя о паре), и правило приватности живёт в одном месте, под
 * тестами.
 *
 * ⚠️ **Единиц оплаты ДВЕ, и приезжают они порознь.** `paid` это оплата ЭТОГО
 * занятия (поурочная пара), `month` это оплата всего месяца, к которому занятие
 * относится (помесячная пара). Вместе они не приходят никогда: режим у пары
 * один. Складывать их нельзя, и разными формами (булево против объекта) они
 * сделаны в том числе поэтому, разбор в `ScheduleEntryMonth` (`@edu/shared`).
 */
export function EntryMarks(props: {
  entry: ScheduleEntry;
  busy: boolean;
  onRun: (fn: () => Promise<void>) => Promise<void>;
}) {
  const { entry } = props;
  /**
   * Почему отметка месяца не случилась, спокойными словами.
   *
   * ⚠️ Не `err` карточки: та печатается красным, а оба отказа этой ручки
   * означают устаревшую сетку, а не ошибку человека. Разбор в `monthMarkNote`.
   */
  const [note, setNote] = useState<string | null>(null);

  /** Что показывать и кому писать. Правило целиком в `marksView`, с тестами. */
  const view = marksView(entry);
  if (view === null) return null;

  const { studentUserId, occurrence, month } = view;

  const set = (patch: { paid?: boolean; attendance?: Attendance | null }): void => {
    if (occurrence === null) return;
    // Объяснение прошлого отказа снимается любым следующим действием: оно про
    // то нажатие, а не про полосу вообще, и висеть над соседней отметкой ему
    // незачем
    setNote(null);
    void props.onRun(() => api.setStudentMark(studentUserId, occurrence.key, patch));
  };

  /**
   * Отметить месяц целиком. Подтверждения нет: нажатие ставит, повторное
   * снимает (решение владельца 21.08.2026, разбор в шапке `StudentMarks.tsx`).
   *
   * ⚠️ Отказ ловится ЗДЕСЬ, внутри обвязки, а не вместо неё: проглотив его, мы
   * оставляем карточке её обычный ход, то есть перечитывание недели. Именно оно
   * и приводит чип в соответствие с тем, что на самом деле у пары.
   */
  const markMonth = (period: string, paid: boolean): void => {
    void props.onRun(async () => {
      setNote(null);
      try {
        await api.setStudentMonth(studentUserId, period, paid);
      } catch (e) {
        const calm = e instanceof api.ApiError ? monthMarkNote(e.code) : null;
        if (calm === null) throw e;
        setNote(calm);
      }
    });
  };

  return (
    <>
      <p className="wk__marks">
        {occurrence?.showAttendance === true && (
          <button
            type="button"
            className="chip"
            disabled={props.busy}
            data-tone={
              entry.attendance === "absent" ? "warn" : entry.attendance === "present" ? "ok" : undefined
            }
            /*
              ⚠️ Состояний ТРИ, поэтому `aria-pressed` тут был бы враньём: он
              двоичный, и «не отмечено» с «не пришёл» слились бы в одно
              «не нажато». Роль переключателя читалке говорит подпись, которая
              перечисляет круг целиком, и она же всплывает подсказкой.
            */
            title="Не отмечено, был, не пришёл"
            onClick={() => set({ attendance: nextAttendance(entry.attendance ?? null) })}
          >
            {entry.attendance === null || entry.attendance === undefined
              ? "был?"
              : ATTENDANCE_TITLES[entry.attendance]}
          </button>
        )}
        {occurrence?.showPaid === true && (
          <button
            type="button"
            className="chip"
            disabled={props.busy}
            // ⚠️ Тревожного цвета у неоплаченного нет: см. шапку `StudentMarks.tsx`
            data-tone={entry.paid === true ? "ok" : undefined}
            // Двоичный переключатель, как и чип месяца рядом: читалка обязана
            // слышать не только подпись, но и то, что она нажата
            aria-pressed={entry.paid === true}
            title={entry.paid === true ? "оплачено, нажмите, чтобы снять" : "отметить оплату занятия"}
            onClick={() => set({ paid: entry.paid !== true })}
          >
            {entry.paid === true ? "оплачено" : "отметить оплату"}
          </button>
        )}
        {month !== null && <MonthChip month={month} busy={props.busy} onMark={markMonth} />}
      </p>
      {note !== null && <p className="note">{note}</p>}
    </>
  );
}

/**
 * Оплата месяца, к которому относится занятие.
 *
 * ⚠️ **Нажатие распоряжается МЕСЯЦЕМ, а не занятием под ним**, и та же отметка
 * стоит чипами в карточке ученика: строка в базе одна, ключ у неё месяц. Отсюда
 * и окно: сервер присылает месяц только в тех пределах, в каких он есть чипами
 * на карточке, иначе отметку, поставленную из расписания на год вперёд, нечем
 * было бы снять (раздел 11.6 `docs/student-marks-design.md`).
 *
 * ⚠️ Тревожного цвета нет: отмеченный месяц зелёный, не отмеченный обычный.
 * `paid: false` означает «месяц не отмечен», а не «есть долг»: платят вперёд, и
 * сентябрь отмечают двадцать пятого августа, когда сентябрьского расписания ещё
 * нет целиком.
 */
function MonthChip(props: {
  month: { period: string; paid: boolean };
  busy: boolean;
  onMark: (period: string, paid: boolean) => void;
}) {
  const { period, paid } = props.month;
  const text = monthChipText(period, paid);

  return (
    <button
      type="button"
      className="chip"
      disabled={props.busy}
      data-tone={paid ? "ok" : undefined}
      aria-pressed={paid}
      title={text.title ?? undefined}
      onClick={() => props.onMark(period, !paid)}
    >
      {text.label}
    </button>
  );
}
